import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Link as MuiLink,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  getApplicationEndPoint,
  getReferralActionRequiredEndPoint,
} from '../../common/api.endPoints';
import { SlaCountdown } from '../../common/components/SlaCountdown';
import { clientEnvVars } from '../../common/environment/clientEnvVars';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { HttpService } from '../../common/http.service';
import { formatReferralStatus } from '../detail/referral/referralSectionUtils';
import AppHeader from '../layout/header/AppHeader';
import { AuthContext } from '../login/AuthWrapper';

export interface ActionRequiredReferralItem {
  applicationId: number;
  referralId: number;
  status: string;
  applicantDisplayName: string;
  termPolicyId: number;
  /** Carrier policy number; display under POLICY ID column header */
  carrierPolicyNumber?: string | null;
  stageName: string | null;
  productName: string | null;
  /** Referral reason; displayed in the Referral Reason column. */
  reason?: string | null;
  referredFromUserId: number;
  referredToUserId: number;
  referredFromDisplayName: string;
  referredToDisplayName: string;
  slaDeadline: string | null;
}

/** True when the current user is the responder (referred to) for this row. */
function isResponder(
  row: ActionRequiredReferralItem,
  currentUserId: number | null,
): boolean {
  return currentUserId != null && row.referredToUserId === currentUserId;
}

/** True when the current user is the requester (referred from) for this row. */
function isRequester(
  row: ActionRequiredReferralItem,
  currentUserId: number | null,
): boolean {
  return currentUserId != null && row.referredFromUserId === currentUserId;
}

/** Parent reason whose stored values may carry a " - <sub-reason>" suffix. */
const REFERRAL_UWA_PARENT = 'Referred to UWA';
const REFERRAL_UWA_PREFIX = `${REFERRAL_UWA_PARENT} - `;

/**
 * Split a stored reason into its parent reason and optional UWA sub-reason.
 * "Referred to UWA - Incorrect evidence received" -> { parent: "Referred to UWA", sub: "Incorrect evidence received" }.
 * Any other reason -> { parent: reason, sub: null }.
 */
function parseReason(reason: string | null | undefined): {
  parent: string;
  sub: string | null;
} {
  const value = reason ?? '';
  if (value.startsWith(REFERRAL_UWA_PREFIX)) {
    return {
      parent: REFERRAL_UWA_PARENT,
      sub: value.slice(REFERRAL_UWA_PREFIX.length),
    };
  }
  return { parent: value, sub: null };
}

/**
 * True when the current user has primary action on this row (highlight with yellow border):
 * - Responder + waiting_for_responder: must reply (SLA).
 * - Requester + waiting_for_requester: must close or follow up.
 * We do not highlight requester + waiting_for_responder (optional top-up; ball is with responder).
 */
function rowNeedsAction(
  row: ActionRequiredReferralItem,
  currentUserId: number | null,
): boolean {
  const s = (row.status || '').toLowerCase();
  if (isResponder(row, currentUserId) && s === 'waiting_for_responder') {
    return true;
  }
  if (isRequester(row, currentUserId) && s === 'waiting_for_requester') {
    return true;
  }
  return false;
}

export const ReferralActionRequiredPage = ({
  embedInDashboard = false,
}: {
  embedInDashboard?: boolean;
}) => {
  const { id: currentUserId } = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isPhase2 = flags.isCaseReferralPhase2Enabled;
  const [items, setItems] = useState<ActionRequiredReferralItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responderFilter, setResponderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [subReasonFilter, setSubReasonFilter] = useState('');
  const [slaSort, setSlaSort] = useState<'oldest' | 'newest'>('oldest');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await HttpService.getInstance().get(
        getReferralActionRequiredEndPoint(),
      );
      if (response?.error) {
        const err = response.error as { message?: string };
        setError(err?.message ?? 'Failed to load referrals.');
        setItems([]);
        return;
      }
      const raw = response?.parsedBody;
      const list = Array.isArray(raw) ? raw : [];
      setItems(list as ActionRequiredReferralItem[]);
    } catch {
      setError('Failed to load referrals.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const responderOptions = useMemo(
    () =>
      Array.from(
        new Set(items.map(i => i.referredToDisplayName).filter(Boolean)),
      ).sort(),
    [items],
  );
  // Collapse "Referred to UWA - <sub>" rows into a single "Referred to UWA" parent option.
  const reasonOptions = useMemo(
    () =>
      Array.from(
        new Set(items.map(i => parseReason(i.reason).parent).filter(Boolean)),
      ).sort(),
    [items],
  );
  // Distinct UWA sub-reasons present in the data (for the dependent sub-filter).
  const uwaSubReasonOptions = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map(i => parseReason(i.reason))
            .filter(r => r.parent === REFERRAL_UWA_PARENT && r.sub)
            .map(r => r.sub as string),
        ),
      ).sort(),
    [items],
  );
  const displayedItems = useMemo(() => {
    const filtered = items.filter(row => {
      if (responderFilter && row.referredToDisplayName !== responderFilter) {
        return false;
      }
      if (statusFilter && (row.status || '').toLowerCase() !== statusFilter) {
        return false;
      }
      if (reasonFilter) {
        const { parent, sub } = parseReason(row.reason);
        if (parent !== reasonFilter) {
          return false;
        }
        if (
          reasonFilter === REFERRAL_UWA_PARENT &&
          subReasonFilter &&
          sub !== subReasonFilter
        ) {
          return false;
        }
      }
      return true;
    });
    const slaTime = (row: ActionRequiredReferralItem) =>
      row.slaDeadline ? new Date(row.slaDeadline).getTime() : null;
    // Sort by SLA. Default oldest-first; toggle to newest. Rows without an SLA always sort last.
    return [...filtered].sort((a, b) => {
      const ta = slaTime(a);
      const tb = slaTime(b);
      if (ta === null && tb === null) {
        return 0;
      }
      if (ta === null) {
        return 1;
      }
      if (tb === null) {
        return -1;
      }
      return slaSort === 'oldest' ? ta - tb : tb - ta;
    });
  }, [
    items,
    responderFilter,
    statusFilter,
    reasonFilter,
    subReasonFilter,
    slaSort,
  ]);

  const workbenchPath = `/${clientEnvVars().NORA_SUBDIR}`;
  const applicationPath = (applicationId: number) =>
    `${workbenchPath}/${getApplicationEndPoint(String(applicationId))}`;

  const filterControls = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <TextField
        select
        size="small"
        label="Responder"
        value={responderFilter}
        onChange={e => setResponderFilter(e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All responders</MenuItem>
        {responderOptions.map(name => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Status"
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All statuses</MenuItem>
        <MenuItem value="waiting_for_responder">Waiting For Responder</MenuItem>
        <MenuItem value="waiting_for_requester">Waiting For Requester</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        label="Referral Reason"
        value={reasonFilter}
        onChange={e => {
          setReasonFilter(e.target.value);
          setSubReasonFilter('');
        }}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All reasons</MenuItem>
        {reasonOptions.map(reason => (
          <MenuItem key={reason} value={reason}>
            {reason}
          </MenuItem>
        ))}
      </TextField>
      {reasonFilter === REFERRAL_UWA_PARENT &&
        uwaSubReasonOptions.length > 0 && (
          <TextField
            select
            size="small"
            label="UWA Sub-reason"
            value={subReasonFilter}
            onChange={e => setSubReasonFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All sub-reasons</MenuItem>
            {uwaSubReasonOptions.map(sub => (
              <MenuItem key={sub} value={sub}>
                {sub}
              </MenuItem>
            ))}
          </TextField>
        )}
      <Button
        size="small"
        variant="outlined"
        onClick={() =>
          setSlaSort(prev => (prev === 'oldest' ? 'newest' : 'oldest'))
        }
      >
        SLA: {slaSort === 'oldest' ? 'Oldest first' : 'Newest first'}
      </Button>
    </Box>
  );

  const tableContent = (
    <>
      {loading ? (
        <Typography color="textSecondary">Loading...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : items.length === 0 ? (
        <Typography color="textSecondary">
          No referrals require your action.
        </Typography>
      ) : (
        <>
          {isPhase2 && filterControls}
          {displayedItems.length === 0 ? (
            <Typography color="textSecondary">
              No referrals match the selected filters.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Applicant
                    </TableCell>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Policy ID
                    </TableCell>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Referral SLA
                    </TableCell>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Requester
                    </TableCell>
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Responder
                    </TableCell>
                    {isPhase2 && (
                      <TableCell
                        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                      >
                        Referral Reason
                      </TableCell>
                    )}
                    <TableCell
                      sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Product
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedItems.map(row => (
                    <TableRow
                      key={`${row.applicationId}-${row.referralId}`}
                      onClick={() =>
                        window.open(
                          applicationPath(row.applicationId),
                          '_blank',
                        )
                      }
                      hover
                      sx={{
                        cursor: 'pointer',
                        ...(rowNeedsAction(row, currentUserId)
                          ? {
                              borderLeft: '4px solid',
                              borderLeftColor: '#F5C842',
                            }
                          : {}),
                      }}
                    >
                      <TableCell>{row.applicantDisplayName || '—'}</TableCell>
                      <TableCell>
                        {row.carrierPolicyNumber ?? row.termPolicyId}
                      </TableCell>
                      <TableCell>
                        {formatReferralStatus(row.status) || '—'}
                      </TableCell>
                      <TableCell>
                        <SlaCountdown
                          sla={
                            (row.status || '').toLowerCase() ===
                              'waiting_for_responder' && row.slaDeadline
                              ? row.slaDeadline
                              : null
                          }
                        />
                      </TableCell>
                      <TableCell>{row.referredFromDisplayName}</TableCell>
                      <TableCell>{row.referredToDisplayName}</TableCell>
                      {isPhase2 && <TableCell>{row.reason ?? '—'}</TableCell>}
                      <TableCell>{row.productName ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  );

  if (embedInDashboard) {
    return <Box sx={{ p: 2 }}>{tableContent}</Box>;
  }

  return (
    <Box>
      <AppHeader />
      <Box sx={{ p: 3 }}>
        <MuiLink
          component={RouterLink}
          to={workbenchPath}
          variant="body2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 2,
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 14 }} />
          Applications
        </MuiLink>
        <Typography variant="h5" sx={{ mb: 2, display: 'block' }}>
          Referral Action Required
        </Typography>
        {tableContent}
      </Box>
    </Box>
  );
};
